import { create } from 'zustand';
import { authApi } from '../../api/auth';
import { decodeUser } from '../../api/jwt';
import { tokenStore } from '../../api/tokenStore';
import type { AuthUser, LoginCredentials, RegisterData, Role } from '../../types/auth';

/**
 * ServiceVault auth store.
 *
 * Identity is derived from the access-token JWT (no /me endpoint). The token
 * lifecycle (15-min access + rotating refresh, single-flight refresh) lives in
 * the HTTP client; this store just tracks the decoded user and exposes role
 * checks. All authorization is re-enforced server-side — these helpers are for
 * UI/routing only.
 */
interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  hydrated: boolean;

  login: (credentials: LoginCredentials) => Promise<AuthUser>;
  /** Exchanges a real Google ID token (obtained client-side, e.g. via Firebase Auth) for a
   * ServiceVault session — same shape as `login`, just a different token-exchange endpoint. */
  loginWithGoogle: (idToken: string) => Promise<AuthUser>;
  /** Guest checkout: provisions a passwordless customer account from just an email + name and
   * signs in as it. Throws ApiError(409, code "email-taken") if the email already has an account. */
  continueAsGuest: (email: string, name: string) => Promise<AuthUser>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  hydrate: () => void;

  hasRole: (role: Role | Role[]) => boolean;
  isAdmin: () => boolean;
  /** Real platform_admin (reserved platform tenant + platform_admin role together — see
   * AuthUser.isPlatformAdmin). Deliberately NOT folded into hasRole('admin')'s "admin is the
   * super-user" bypass below: a store's own "admin" must never satisfy this, only a genuine
   * platform admin can. */
  isPlatformAdmin: () => boolean;
  isAuthenticated: () => boolean;
  getDisplayName: () => string;
  getUserInitials: () => string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  hydrated: false,

  login: async (credentials) => {
    set({ isLoading: true });
    try {
      const pair = await authApi.login(credentials);
      const user = decodeUser(pair.accessToken);
      if (!user) throw new Error('Received an invalid token from the server.');
      set({ user });
      return user;
    } finally {
      set({ isLoading: false });
    }
  },

  loginWithGoogle: async (idToken) => {
    set({ isLoading: true });
    try {
      const pair = await authApi.google(idToken);
      const user = decodeUser(pair.accessToken);
      if (!user) throw new Error('Received an invalid token from the server.');
      set({ user });
      return user;
    } finally {
      set({ isLoading: false });
    }
  },

  continueAsGuest: async (email, name) => {
    set({ isLoading: true });
    try {
      const pair = await authApi.guest(email, name);
      const user = decodeUser(pair.accessToken);
      if (!user) throw new Error('Received an invalid token from the server.');
      set({ user });
      return user;
    } finally {
      set({ isLoading: false });
    }
  },

  register: async (data) => {
    await authApi.register({
      email: data.email,
      name: data.name,
      password: data.password,
      role: data.role ?? 'customer',
    });
  },

  logout: () => {
    authApi.logout();
    set({ user: null });
  },

  /** Restore the user from a persisted token on app start. */
  hydrate: () => {
    const tokens = tokenStore.get();
    const user = tokens ? decodeUser(tokens.accessToken) : null;
    set({ user, hydrated: true });
  },

  hasRole: (role) => {
    const roles = get().user?.roles ?? [];
    if (roles.includes('admin')) return true; // admin is the super-user
    const wanted = Array.isArray(role) ? role : [role];
    return wanted.some((r) => roles.includes(r));
  },

  isAdmin: () => (get().user?.roles ?? []).includes('admin'),
  isPlatformAdmin: () => get().user?.isPlatformAdmin === true,
  isAuthenticated: () => get().user !== null,
  getDisplayName: () => get().user?.name ?? get().user?.email ?? 'Guest',
  getUserInitials: () => initials(get().user?.name ?? get().user?.email ?? 'G'),
}));

// Keep the store in sync if the token is cleared elsewhere (e.g. failed refresh).
tokenStore.subscribe((tokens) => {
  if (!tokens) useAuthStore.setState({ user: null });
});
